import React, { useState } from 'react';
import { useProjectStore } from '../../hooks/useProjectStore';
import {
  ECO_PARAMS, useActiveDesign, computeCost, computeProduction, computeNPV,
  ETH, ecoInputStyle,
} from './ecoShared';

// /economics/npv — NPV / IRR / 回收期 + 逐年现金流
export default function EcoNpvPage() {
  const { currentProject } = useProjectStore();
  const { fluid, prop } = useActiveDesign(currentProject?.id);
  const [, force] = useState(0);
  const p = ECO_PARAMS;
  const set = (k: keyof typeof p, v: number) => { (p[k] as number) = v; force((x) => x + 1); };

  const { total: capex } = computeCost(p, fluid, prop);
  const { annual } = computeProduction(p);
  const { npv, irr, payback, cumRows } = computeNPV(p, capex, annual);

  if (!currentProject) return <div style={{ padding: 40, color: ETH.textSec }}>请先选择项目</div>;

  const kpi = (label: string, val: string, color: string, sub?: string) => (
    <div style={{ flex: 1, padding: '16px 18px', background: ETH.panelHeaderBg, borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: ETH.textMuted }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color, fontFamily: ETH.fontMono }}>{val}</div>
      {sub && <div style={{ fontSize: 11, color: ETH.textSec }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: ETH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${ETH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: ETH.text, margin: 0 }}>经济评价 (NPV / IRR / 回收期)</h2>
        <span style={{ fontSize: 12, color: ETH.textMuted }}>投资 {capex.toFixed(0)} 万元(来自成本构成页)</span>
      </div>

      <div style={{ padding: '10px 20px', display: 'flex', gap: 16, flexWrap: 'wrap', borderBottom: `1px solid ${ETH.headerBorder}`, background: ETH.panelHeaderBg }}>
        <Lab t="产品价格 元/m³"><input type="number" value={p.price} style={ecoInputStyle} onChange={(e) => set('price', +e.target.value)} /></Lab>
        <Lab t="年操作成本 万元"><input type="number" value={p.opex} style={ecoInputStyle} onChange={(e) => set('opex', +e.target.value)} /></Lab>
        <Lab t="贴现率 %"><input type="number" value={p.discount} style={ecoInputStyle} onChange={(e) => set('discount', +e.target.value)} /></Lab>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, maxWidth: 720 }}>
          {kpi('净现值 NPV', `${npv.toFixed(0)}`, npv >= 0 ? ETH.ok : ETH.accent, '万元 (贴现后)')}
          {kpi('内部收益率 IRR', irr != null ? `${irr.toFixed(1)}%` : '—', irr != null && irr >= p.discount ? ETH.ok : ETH.warn, irr != null ? `贴现率 ${p.discount}%` : '现金流不收敛')}
          {kpi('回收期', payback != null ? `${payback.toFixed(1)}` : '—', ETH.stage, payback != null ? '年' : '期内未回收')}
        </div>

        <table style={{ width: '100%', maxWidth: 720, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: ETH.panelHeaderBg }}>
            {['年', '净现金流 万元', '累计现金流 万元'].map((h) => <th key={h} style={{ padding: '6px 12px', textAlign: 'right', borderBottom: `1px solid ${ETH.headerBorder}`, color: ETH.textSec }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {cumRows.map((r) => (
              <tr key={r.year} style={{ borderBottom: '1px solid #F0EDE4' }}>
                <td style={td}>{r.year}</td>
                <td style={td}>{r.cf.toFixed(0)}</td>
                <td style={{ ...td, color: r.cum >= 0 ? ETH.ok : ETH.accent, fontWeight: 600 }}>{r.cum.toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Lab({ t, children }: { t: string; children: React.ReactNode }) {
  return <label style={{ fontSize: 11, color: ETH.textSec, display: 'flex', flexDirection: 'column', gap: 3 }}>{t}{children}</label>;
}
const td: React.CSSProperties = { padding: '5px 12px', textAlign: 'right', fontFamily: ETH.fontMono, color: ETH.text };
